import React, { useCallback, useState } from "react";
import { Dialog, Button, Text } from "@radix-ui/themes";
import { XCircle } from "@geist-ui/icons";

function ErrorModalComponent({
  isOpen,
  onClose,
  title = "An error occurred",
  description = "Something went wrong. Please try again later.",
  actionText = "Close",
  onAction,
  disableBackgroundClick = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  actionText?: string;
  onAction?: () => void;
  disableBackgroundClick?: boolean;
}) {
  const handleAction = () => {
    if (onAction) {
      onAction();
    }
    onClose();
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={disableBackgroundClick ? () => {} : onClose}
    >
      <Dialog.Content className="p-4 ">
        <div className="flex flex-col items-center p-2">
          <div className="text-red-500 mb-4">
            <XCircle size={48} />
          </div>

          <Dialog.Title className="text-center mb-2">{title}</Dialog.Title>

          <Text className="text-center mb-6 text-gray-600">{description}</Text>

          <div className="w-full flex justify-center">
            <Button color="red" size="3" onClick={handleAction}>
              {actionText}
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

export function useErrorModal() {
  const [isOpen, setIsOpen] = useState(false);

  const openToggle = () => setIsOpen((prev) => !prev);

  const ErrorModal = useCallback(
    ({
      title = "An error occurred",
      description = "Something went wrong. Please try again later.",
      actionText = "Close",
      onAction,
      disableBackgroundClick = false,
    }: {
      title?: string;
      description?: string;
      actionText?: string;
      onAction?: () => void;
      disableBackgroundClick?: boolean;
    } = {}) => {
      return (
        <ErrorModalComponent
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title={title}
          description={description}
          actionText={actionText}
          onAction={onAction}
          disableBackgroundClick={disableBackgroundClick}
        />
      );
    },
    [isOpen]
  );

  return { isOpen, openToggle, ErrorModal };
}
